import { format } from 'date-fns';
import {
  default as React,
  useCallback,
  useEffect,
  useMemo,
  useState
} from 'react';
import { Animated, useWindowDimensions } from 'react-native';
import styled from 'styled-components/native';

import {
  RouteProp,
  useFocusEffect,
  useNavigation
} from '@react-navigation/core';
import { useRoute } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';

import ActivityIndicatorScreen from '../../components/ActivityIndicatorScreen';
import Button from '../../components/Button';
import Spacer from '../../components/Spacer';
import { H5 } from '../../components/Typography';
import { StudiesParamList } from '../../navigation/StudiesNavigator';
import StudyStartCard from './cards/StudyStartCard';
import StudyOverviewHeader from './components/StudyOverviewHeader';
import { StudyOverviewCard } from './screen-components/plugins/StudyOverviewCardPlugin';
import {
  StudyContextProvider,
  useStudyContext
} from '../../context/StudyContext';
import { defaultScreenComponentPlugins } from './screen-components/plugins/default';
import AlertBox from '../../components/AlertBox';
import { useUserContext } from '../../context/UserContext';
import { calculateStudyParticipantDates } from '../../common/calculateStudyParticipantDates';
import { hasCompletedFormTemplateFields } from '../../common/hasCompletedFormTemplateFields';
import { ScreenComponentVisibility } from '../../models/ScreenComponent';
import { useAppContext } from '../../AppContext';
import { getDayNightBackgroundSource } from '../../common/DayNightUtils';
import elementIDs from '../../../elementIDs';
import {
  UserAttributesProvider,
  useUserAttributesContext
} from '../../context/UserAttributesContext';

const StyledContainerView = styled.View`
  width: 100%;
  height: 100%;
  background-color: #fdfbf7;
`;

const StyledDataNotFoundContainerView = styled.View`
  width: 100%;
  height: 100%;
  align-items: center;
  justify-content: center;
`;

const StyledScrollView = styled.ScrollView`
  width: 100%;
  height: 100%;
`;

const StyledContentView = styled.View<{
  windowHeight: number;
}>`
  width: 100%;
  max-width: 830px;
  padding: 24px 18px;
  padding-bottom: 64px;
  align-self: center;
`;

const StyledContentRow = styled.View`
  flex-flow: row wrap;
  justify-content: space-between;
  margin-top: 16px;
`;

export interface StudyInforScreenProps {
  navigation: any;
}

const StudyScreen: React.FC = () => {
  const route = useRoute<RouteProp<StudiesParamList, 'StudyInfo'>>();
  const studyParticipantId = route.params.studyParticipantId;
  const studyId = route.params.studyId;
  return (
    <StudyContextProvider
      studyParticipantId={studyParticipantId}
      studyId={studyId}
    >
      <UserAttributesProvider>
        <StudyView />
      </UserAttributesProvider>
    </StudyContextProvider>
  );
};

const StudyView: React.FC = () => {
  const [mounted, setMounted] = useState(false);
  const { setHeaderConfig, scrollY } = useAppContext();
  const { user, updateUserSession } = useUserContext();
  const navigation = useNavigation<StackNavigationProp<any>>();
  const window = useWindowDimensions();

  const {
    latestAttributes,
    connectedWearableSources,
    checkConnectedWearables,
    synchronizingData,
    loadingLatestAttributes,
    syncData
  } = useUserAttributesContext();

  const {
    studyParticipantId,
    studyIntake,
    loadingStudy,
    loadingStudyIntake,
    getStudyIntake,
    getStudy,
    study
  } = useStudyContext();
  // load the study intake on focus
  useFocusEffect(
    useCallback(() => {
      checkConnectedWearables();
      updateUserSession(); // refresh user (to get updated reward points) and study participant records
      if (studyParticipantId) getStudyIntake(studyParticipantId);
      getStudy();
    }, [updateUserSession, studyParticipantId, getStudyIntake, getStudy])
  );

  useEffect(() => {
    if (
      user &&
      study?.screenComponents?.find(
        c => c.type === 'WearableDataLineChartCard'
      ) &&
      connectedWearableSources?.length
    ) {
      syncData(user.id, connectedWearableSources, false, true);
    }
  }, [study, user?.id, connectedWearableSources]);

  // second focus effect for updating header config on initial load and when studyTemplate is updated
  // NOTE: separate it so it doesn't cause session to update when studyTemplate object is refreshed
  useFocusEffect(
    useCallback(() => {
      setHeaderConfig({
        title: 'My Results',
        backgroundSource:
          study?.backgroundImageUrl || getDayNightBackgroundSource(),
        foregroundColor: study?.foregroundColor
      });
    }, [setHeaderConfig, study?.backgroundImageUrl, study?.foregroundColor])
  );

  const { notificationTime } = studyIntake || {};

  const studyErrors = [];
  if (!notificationTime)
    studyErrors.push("You didn't specify a notification time.");

  const studyParticipant = studyIntake?.studyParticipant;

  const {
    startDate,
    projectedEndDate,
    studyLength,
    isCompletedStudy,
    projectedStudyLength,
    isProjectedComplete
  } = calculateStudyParticipantDates(study, studyParticipant, true);

  // use actual startDate to check study progress
  const status = studyParticipant?.withdrawnDate
    ? 'withdrawn'
    : studyParticipant?.screenFailedDate
      ? 'screenFailed'
      : isProjectedComplete
        ? 'completed'
        : studyParticipant?.paused
          ? 'paused'
          : studyParticipant?.startDate
            ? 'progress'
            : 'planning';
  const studyName = study?.name || 'Your Study';
  const intakeMatchesParticipant =
    studyParticipantId == null ||
    studyIntake?.studyParticipantId === studyParticipantId;

  // Display the study intake form if sponsored study and intake form is not
  // completed (all required fields are not filled in)
  useEffect(() => {
    const timeoutId = setTimeout(() => setMounted(true), 10000);
    if (intakeMatchesParticipant) {
      if (
        // study?.type === StudyType.Sponsor &&
        studyIntake?.formTemplate &&
        !hasCompletedFormTemplateFields(
          studyIntake.formTemplate,
          studyIntake,
          studyIntake.studyParticipant?.user ?? user
        )
      ) {
        navigation.navigate('ModalNavigator', {
          screen: 'FormTemplate',
          params: {
            formTemplateId: studyIntake?.formTemplateId,
            entityDataId: studyIntake?.id,
            studyParticipantId: studyIntake.studyParticipantId,
            fromStudyScreen: true
          }
        });
      } else if (studyIntake) {
        // show study details
        setMounted(true);
      }
    }
    return () => clearTimeout(timeoutId);
  }, [intakeMatchesParticipant, navigation, studyIntake, user]);

  const studyLoading = useMemo(
    () => loadingStudy || loadingStudyIntake,
    [loadingStudy, loadingStudyIntake]
  );

  return (
    <StyledContainerView>
      {study && studyIntake && intakeMatchesParticipant && mounted ? (
        <StyledScrollView
          // Fixes bug with scrollbar in middle of screen
          scrollIndicatorInsets={{ right: 1 }}
          // get scroll offset for app header animation
          onScroll={Animated.event(
            [{ nativeEvent: { contentOffset: { y: scrollY } } }],
            { useNativeDriver: false }
          )}
          scrollEventThrottle={16}
        >
          <StyledContentView windowHeight={window.height}>
            <StudyOverviewHeader
              organizationName={study.organization?.name}
              display='daysLogged'
              studyName={studyName}
              status={status}
              startDate={startDate}
              endDate={projectedEndDate}
              studyParticipantId={studyParticipant?.id}
              studyLength={projectedStudyLength ?? studyLength}
              isCompletedStudy={isProjectedComplete}
              description={study?.description || ''}
              // studyType={study.type}
              isEnableShareResult={study.enableShareResult}
              onEdit={() => {
                if (isCompletedStudy) return;
                navigation.navigate('StudiesNav', {
                  screen: 'StudyEdit',
                  params: {
                    studyName,
                    allowSelfWithdraw: study.allowSelfWithdraw,
                    // studyType: study.type,
                    startDate: startDate
                      ? format(startDate, 'MM/dd/yyyy')
                      : undefined,
                    studyParticipantId: studyParticipant?.id,
                    studyId: studyParticipant?.studyId,
                    type: status,
                    studyLength,
                    notificationTime
                  }
                });
              }}
            />
            {status !== 'paused' &&
              status !== 'progress' &&
              status !== 'withdrawn' &&
              status !== 'screenFailed' &&
              !isCompletedStudy &&
              studyParticipant && (
                <StyledContentRow>
                  <StudyStartCard
                    studyErrors={studyErrors}
                    studyParticipant={studyParticipant}
                  />
                </StyledContentRow>
              )}
            {study?.screenComponents?.map(component => {
              const plugin = defaultScreenComponentPlugins.find(
                p => p.type === component.type
              );
              if (
                // retain old logic if visiblity is not configured
                (!component?.visibility &&
                  status === 'planning' &&
                  plugin?.categories?.includes('study-progress')) ||
                // hide components marked for completed study while study is in progress
                (!isCompletedStudy &&
                  component?.visibility ==
                    ScreenComponentVisibility.CompletedStudy) ||
                // hide components marked for in progress if the study is in planning or is completed
                ((status === 'planning' || isCompletedStudy) &&
                  component?.visibility == ScreenComponentVisibility.InProgress)
              ) {
                return null;
              }
              return (
                <StyledContentRow
                  testID={elementIDs.genericCardID}
                  key={component.id}
                >
                  {plugin ? (
                    <plugin.component
                      {...(plugin.type === 'WearableDataLineChartCard'
                        ? {
                            latestAttributes: latestAttributes || [],
                            connectedWearableSources:
                              connectedWearableSources || [],
                            loading:
                              synchronizingData || loadingLatestAttributes
                          }
                        : {})}
                      studyParticipant={studyParticipant}
                      component={component}
                      {...(component.props || {})}
                    />
                  ) : (
                    <AlertBox severity='error'>
                      &quot;{component.name || component.type}&quot; component
                      not found.
                    </AlertBox>
                  )}
                </StyledContentRow>
              );
            })}
            {(!study?.screenComponents ||
              study.screenComponents.length === 0) && (
              <StyledContentRow>
                <StudyOverviewCard studyParticipant={studyParticipant} />
              </StyledContentRow>
            )}
          </StyledContentView>
        </StyledScrollView>
      ) : (
        mounted &&
        !studyLoading && (
          <StyledDataNotFoundContainerView>
            <H5 color='dark'>Study Data Not Found</H5>
            <Spacer size={3} />
            <Button
              outlined
              size='small'
              onPress={() => {
                navigation.goBack();
              }}
            >
              Back
            </Button>
          </StyledDataNotFoundContainerView>
        )
      )}
      {(studyLoading || !mounted) && <ActivityIndicatorScreen modal />}
    </StyledContainerView>
  );
};

export default StudyScreen;
